import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useCookieConsent } from "@/stores/useCookieConsent";

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const { showBanner, acceptAll, rejectAll, openPreferences } = useCookieConsent();
  const [isVisible, setIsVisible] = useState(false);

  // アニメーション用（マウント後に表示）
  useEffect(() => {
    if (showBanner) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }
    setIsVisible(false);
  }, [showBanner]);

  if (!showBanner) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      aria-modal="false"
      className={`
        fixed bottom-0 left-0 right-0 z-50
        md:bottom-4 md:left-4 md:right-auto md:max-w-md
        transition-all duration-300
        ${isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}
      `}
    >
      <div className="bg-background border-t md:border md:rounded-lg shadow-lg p-4 md:p-6 space-y-3 md:space-y-4 max-w-full">
        {/* タイトル */}
        <div className="flex items-start gap-2 md:gap-3">
          <span className="text-xl md:text-2xl flex-shrink-0" aria-hidden="true">
            🍪
          </span>
          <div className="flex-1 min-w-0">
            <h2 id="cookie-banner-title" className="text-base md:text-lg font-semibold">
              {t("cookieConsent.banner.title")}
            </h2>
          </div>
        </div>

        {/* 説明文 */}
        <p id="cookie-banner-description" className="text-sm text-muted-foreground break-words">
          {t("cookieConsent.banner.description")}
        </p>

        {/* プライバシーポリシーリンク */}
        <a
          href="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary hover:underline block"
        >
          {t("cookieConsent.banner.privacyPolicyLink")}
        </a>

        {/* ボタングループ（ダークパターン対策: 全て同等の視覚的重み） */}
        <div className="flex flex-col gap-2 md:flex-row md:gap-3">
          {/* 全て拒否 */}
          <Button
            variant="outline"
            onClick={rejectAll}
            className="h-10 md:flex-1 text-sm whitespace-nowrap"
            aria-label={t("cookieConsent.banner.rejectAllAria")}
          >
            {t("cookieConsent.banner.rejectAll")}
          </Button>

          {/* 詳細設定 */}
          <Button
            variant="outline"
            onClick={openPreferences}
            className="h-10 md:flex-1 text-sm whitespace-nowrap"
            aria-label={t("cookieConsent.banner.preferencesAria")}
          >
            {t("cookieConsent.banner.preferences")}
          </Button>

          {/* 全て許可 */}
          <Button
            variant="default"
            onClick={acceptAll}
            className="h-10 md:flex-1 text-sm whitespace-nowrap"
            aria-label={t("cookieConsent.banner.acceptAllAria")}
          >
            {t("cookieConsent.banner.acceptAll")}
          </Button>
        </div>
      </div>
    </div>
  );
}
